<template>
    <section :class="['slice', 'slice-resources-list', `slice-resources-list--bg-${props.slice.primary.background}`]">
        <div class="slice-content">
            <PrismicRichText :field="slice.primary.title" class="slice-resources-list__title" />
            <PrismicRichText :field="slice.primary.headline" class="slice-resources-list__headline" />

            <div class="slice-resources-list__grid">
                <template v-for="{item, config} in cardItems" :key="item.id">
                    <Component :is="config.component" v-bind="{[config.type]: item}" />
                </template>
            </div>
            <div v-if="isFilled.link(slice.primary.link)" class="slice-resources-list__cta">
                <VJoyButton variant="main" :href="$withLocalePrefix(slice.primary.link.url!)">
                    {{ (slice.primary.link as {text?: string}).text }}
                </VJoyButton>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, type Component} from 'vue';
    import type {Content} from '@prismicio/client';
    import {filter} from '@prismicio/client';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import {useAsyncData} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ArticleCard from '~/components/ArticleCard.vue';
    import CollectionCard from '~/components/CollectionCard.vue';
    import EventCard from '~/components/EventCard.vue';
    import GuideCard from '~/components/GuideCard.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';
    import {baseSearchPredicatesByCustomType} from '~/utils/searchPredicates';
    import {getResourceListBasePredicates, getResourceListCardType, getResourceListPrismicType, type ResourceCardType} from './resourcesList';

    const props = defineProps(getSliceComponentProps<Content.ResourcesListSlice>(['slice', 'index', 'slices', 'context']));

    const CARD_CONFIG: Record<ResourceCardType, {component: Component; type: string}> = {
        article: {component: ArticleCard, type: 'article'},
        successStory: {component: ArticleCard, type: 'article'},
        event: {component: EventCard, type: 'event'},
        guide: {component: GuideCard, type: 'guide'},
        collection: {component: CollectionCard, type: 'collection'},
    };

    function getCardConfig(type: string) {
        const cardType = getResourceListCardType(type);
        return cardType ? CARD_CONFIG[cardType] : null;
    }

    const manualListedItems = props.slice.primary.list.map((row) => row.item).filter((item) => isFilled.contentRelationship(item));

    function fetchFallbackDocuments() {
        const excludeIds = manualListedItems.map((item) => item.id);
        const needToFetch = Math.max(0, 3 - manualListedItems.length);
        const resourceType = props.slice.primary.resourceType;

        if (needToFetch === 0) return Promise.resolve([]);

        const prismicType = getResourceListPrismicType(resourceType);
        if (!prismicType) return Promise.resolve([]);

        const eventPredicates = resourceType === 'events' ? getEventPredicates() : [];
        const basePredicates = getResourceListBasePredicates(prismicType, baseSearchPredicatesByCustomType);

        return usePrismicSearch({
            type: prismicType,
            pageSize: 3,
            excludes: excludeIds,
            predicates: [...basePredicates, ...eventPredicates],
            ...(resourceType === 'events' && {orderings: [{field: 'my.event.date', direction: 'asc'}]}),
        }).then((query) => query?.results ?? []);
    }

    function getEventPredicates() {
        const yesterdayDate = new Date();
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const yesterday = yesterdayDate.toISOString().split('T')[0];

        return [filter.dateAfter('my.event.date', yesterday), filter.any('my.event.eventType', ['offline', 'online'])];
    }

    const {data: fallbackPayload} = await useAsyncData(`resources-list-fallback-${props.slice.primary.resourceType}`, fetchFallbackDocuments, {
        deep: true,
    });

    const displayItems = computed(() => {
        const fallbackDocuments = fallbackPayload.value ?? [];
        return [...manualListedItems, ...fallbackDocuments].slice(0, 3);
    });

    const cardItems = computed(() =>
        displayItems.value.flatMap((item) => {
            const config = getCardConfig(item.type);
            return config ? [{item, config}] : [];
        }),
    );
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-resources-list .slice-content {
        text-align: left;
    }

    .slice-resources-list {
        &--bg-light-red {
            background-color: var(--joy-color-primary-10);
        }

        &--bg-white {
            background-color: var(--joy-color-background-default);
        }

        &__title {
            text-align: center;
            margin-bottom: var(--joy-core-spacing-5);

            p {
                color: var(--joy-color-primary-50);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
            }
        }

        &__headline {
            text-align: center;
            margin-bottom: var(--joy-core-spacing-10);
        }

        &__grid {
            display: grid;
            row-gap: var(--joy-core-spacing-5);
            grid-template-columns: minmax(0, 1fr);
            margin-bottom: var(--joy-core-spacing-9);

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                column-gap: var(--joy-core-spacing-3);
                row-gap: var(--joy-core-spacing-4);

                & > :last-child {
                    grid-column: 1 / -1;
                    max-width: 50%;
                    margin: 0 auto;
                }
            }
            @include mq.screen_lg(min) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                column-gap: var(--joy-core-spacing-4);
                row-gap: var(--joy-core-spacing-6);
                margin-bottom: 70px;

                & > :last-child {
                    grid-column: auto;
                    max-width: 100%;
                }
            }
        }

        &__cta {
            display: flex;
            justify-content: center;
        }
    }
</style>
